import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

// After a redeploy the hashed chunk filenames change and the old chunks 404.
// A tab opened before the deploy (or a browser/CDN that served a stale
// index.html) then fails its first dynamic import with a "Failed to fetch
// dynamically imported module" / "error loading dynamically imported module"
// TypeError, which bubbles to the ErrorBoundary as "Something went wrong".
//
// lazyWithReload retries the import once, then force-reloads the page a single
// time (guarded by sessionStorage so we never loop) to pull a fresh
// index.html with valid chunk hashes. This is the standard Vite stale-chunk
// recovery and turns a hard error into a transparent reload.

const RELOAD_KEY = 'appnz:chunk-reload-at';
const RELOAD_WINDOW_MS = 15_000;

function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /'?text\/html'? is not a valid JavaScript MIME type/i.test(msg)
  );
}

export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (!isChunkLoadError(err) || typeof window === 'undefined') throw err;
      // Retry once in case it was a transient network blip rather than a deploy.
      try {
        return await factory();
      } catch (retryErr) {
        if (!isChunkLoadError(retryErr)) throw retryErr;
        let last = 0;
        try {
          last = Number(window.sessionStorage.getItem(RELOAD_KEY) || 0);
        } catch {
          /* sessionStorage blocked (private mode) — fall through and reload */
        }
        if (Date.now() - last > RELOAD_WINDOW_MS) {
          try {
            window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
          } catch {
            /* ignore */
          }
          window.location.reload();
          // Keep Suspense pending until the reload swaps the document.
          return new Promise<{ default: T }>(() => {});
        }
        // Already reloaded recently and still failing — surface the real error.
        throw retryErr;
      }
    }
  });
}
